import { FindingsLabel } from '@app/icare/components/findings'
import { PatientADTeamEditDrawer } from '@app/icare/entities/patient/edits/PatientADTeamEditDrawer'
import { PatientCareManagerEditDrawer } from '@app/icare/entities/patient/edits/PatientCareManagerEditDrawer'
import { selectByGroup } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import type { PatientADTeamRelation } from '@app/icare/entities/patient/schema/PatientADTeam'
import type { PatientCareManagerRelation } from '@app/icare/entities/patient/schema/PatientCareManager'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { AddButton } from '@pkg/ui/button'
import { DrawerSaveDropdown } from '@pkg/ui/drawer/DrawerSaveDropdown'
import { useRouterTabs } from '@pkg/ui/hooks'
import { useExtra, useTabs } from '@pkg/ui/layout/PageContainer'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useRelationCommitNavigation } from './-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/zustandigkeit')({
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const tabs = useRouterTabs({ basePath: `/patienten/${id}/zustandigkeit` })

  useTabs(
    {
      ...tabs,
      items: [
        {
          key: '/',
          label: t`Übersicht`,
        },
        {
          key: '/ad-team',
          label: <FindingsLabel label={t`AD-Team`} selector={selectByGroup('adTeams')} />,
        },
        {
          key: '/versorgungsmanager',
          label: <FindingsLabel label={t`Versorgungsmanager`} selector={selectByGroup('careManagers')} />,
        },
      ],
    },
    [tabs.activeKey],
  )

  const handleAfterCommitAdTeam = useRelationCommitNavigation<PatientADTeamRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: _relation =>
      navigate({
        to: '/patienten/$id/zustandigkeit/ad-team',
        params: { id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '/patienten/$id/zustandigkeit/ad-team', params: { id } }),
  })
  const handleAfterCommitCareManager = useRelationCommitNavigation<PatientCareManagerRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: _relation =>
      navigate({
        to: '/patienten/$id/zustandigkeit/versorgungsmanager',
        params: { id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '/patienten/$id/zustandigkeit/versorgungsmanager', params: { id } }),
  })

  useExtra(
    <DrawerSaveDropdown
      items={[
        {
          key: 'add-ad-team',
          label: t`AD-Team`,
          onClick: () => PatientADTeamEditDrawer.show({ patientId: id, onAfterCommit: handleAfterCommitAdTeam }),
        },
        {
          key: 'add-care-manager',
          label: t`Versorgungsmanager:in`,
          onClick: () =>
            PatientCareManagerEditDrawer.show({ patientId: id, onAfterCommit: handleAfterCommitCareManager }),
        },
      ]}
    >
      <AddButton>
        <Trans>Zuständigkeit hinzufügen</Trans>
      </AddButton>
    </DrawerSaveDropdown>,
  )

  return <Outlet />
}
